// Favourites (C315, X7-11b): records the person pins for quick return. This browser keeps the fast, offline copy; the list
// is also saved to the person's preferences on the server, so the pins follow the person to other devices. Storage rules
// live in features/search/localList.ts; the server exchange lives in favourites-sync.ts.
import { serverAuth } from "@/data/auth";
import { createLocalList } from "@/features/search/localList";
import { isRecordRef, refOf, sameRecord, type RecordRef } from "@/features/search/recordIndex";
import { FAVOURITES_LIMIT, reconcileFavourites } from "./favourites-reconcile";
import { readServerFavourites, writeServerFavourites } from "./favourites-sync";

const favouriteList = createLocalList<RecordRef>({ key: "os-favourites", limit: FAVOURITES_LIMIT, same: sameRecord, isItem: isRecordRef });

/** Pins the record, or unpins it when it is already pinned. Returns the new list and saves it to the server (best effort). */
export function toggleFavourite(record: RecordRef): RecordRef[] {
  const next = favouriteList.toggle(refOf(record));
  if (serverAuth) void writeServerFavourites(next).catch(() => undefined);
  return next;
}

/** True when the record is pinned. */
export function isFavourite(record: RecordRef): boolean {
  return favouriteList.has(refOf(record));
}

/** The pinned records, newest pin first. Re-renders the component when the list changes. */
export function useFavourites(): RecordRef[] {
  return favouriteList.useList();
}

/** Brings the pins saved on the server into this browser (called at start-up on staff pages). A failed sync changes nothing: the browser copy keeps working. */
export async function syncFavourites(): Promise<void> {
  if (!serverAuth) return;
  try {
    const { list, push } = reconcileFavourites(await readServerFavourites(), favouriteList.get());
    favouriteList.replace(list);
    if (push) await writeServerFavourites(list);
  } catch {
    // nothing shown: the next start-up tries again
  }
}
